import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MessageSquare, Sparkles } from "lucide-react";
import useSeo from "../lib/useSeo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { BRAND } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_TYPES = [
  "Clarity Kit",
  "Diagnostic",
  "Clarity Sprint",
  "AI Workshop",
  "AI Pilot Advisory",
  "Something else",
];

const NEXT_STEPS = [
  "Try the Kit",
  "Book Diagnostic",
  "Ask a question",
];

const initialForm = {
  name: "",
  email: "",
  business: "",
  website: "",
  inquiry_type: "",
  preferred_next_step: "",
  message: "",
};

export default function Contact() {
  useSeo({
    title: "Contact · BuildOnce Studio",
    description:
      "Tell us where you are. Book a discovery call, ask a question, or start with the free Clarity Check. BuildOnce Studio, Singapore.",
  });
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        business: form.business || null,
        website: form.website || null,
        inquiry_type: form.inquiry_type || null,
        preferred_next_step: form.preferred_next_step || null,
        message: form.message,
      });
      setDone(true);
      setForm(initialForm);
      toast.success("Note received. We will reply within one working day.");
    } catch (err) {
      toast.error("Something did not go through. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-bos-ice">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-20 lg:pt-24 pb-20">
          <p className="bos-eyebrow">Contact</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-medium text-bos-navy tracking-tight leading-[1.08] max-w-3xl">
            Not sure where to start? <span className="text-bos-primary">Tell us where you are.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[17px] text-bos-charcoal/85 leading-[1.75]">
            If you are still exploring, start with the Free Clarity Check.
            If you already have a direction and want a second pair of eyes,
            ask about the BuildOnce Diagnostic.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/clarity-diagnostic"
              data-testid="contact-cta-try"
              className="inline-flex items-center gap-2 bg-bos-navy text-bos-white px-6 py-3 rounded-full hover:bg-bos-primary transition-colors text-[14px]"
            >
              Try the Free Clarity Check <ArrowRight size={14} />
            </Link>
            <a
              href="#ask"
              data-testid="contact-cta-ask"
              className="inline-flex items-center gap-2 border border-bos-navy text-bos-navy px-6 py-3 rounded-full hover:bg-bos-navy hover:text-bos-white transition-colors text-[14px]"
            >
              Ask a Question
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-[1fr_1.4fr] gap-16">
        <div className="space-y-10">
          <div data-testid="contact-channel-email">
            <div className="inline-flex items-center gap-2 text-bos-mid">
              <Mail size={16} />
              <span className="bos-eyebrow">Email</span>
            </div>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email-link"
              className="mt-3 block text-[22px] text-bos-navy bos-link-underline"
            >
              {BRAND.email}
            </a>
            <p className="mt-3 text-[14px] text-bos-charcoal/70">
              Best for briefs, considered questions, or sharing context.
            </p>
          </div>

          <div data-testid="contact-channel-call">
            <div className="inline-flex items-center gap-2 text-bos-mid">
              <MessageSquare size={16} />
              <span className="bos-eyebrow">Book a discovery call</span>
            </div>
            <Link
              to="/booking"
              data-testid="contact-book-link"
              className="mt-3 inline-flex items-center gap-2 text-[18px] text-bos-navy bos-link-underline"
            >
              Book a discovery call <ArrowRight size={14} />
            </Link>
            <p className="mt-3 text-[14px] text-bos-charcoal/85 leading-[1.7] max-w-md">
              A 30-minute conversation to understand what you are working on
              and whether BuildOnce Studio is the right fit for where you are
              now. You will leave knowing whether there is a clear next step,
              and what it is.
            </p>
          </div>

          <div data-testid="contact-channel-diagnostic" className="pt-8 border-t border-bos-ice">
            <div className="inline-flex items-center gap-2 text-bos-mid">
              <Sparkles size={16} />
              <span className="bos-eyebrow">BuildOnce Diagnostic · SGD 450</span>
            </div>
            <Link
              to="/booking"
              data-testid="contact-diagnostic-link"
              className="mt-3 inline-flex items-center gap-2 text-[18px] text-bos-navy bos-link-underline"
            >
              Book the Diagnostic <ArrowRight size={14} />
            </Link>
            <p className="mt-3 text-[14px] text-bos-charcoal/85 leading-[1.7] max-w-md">
              A 75-minute working session for founders who already have a
              rough direction but want a second pair of eyes. Ends with a
              written summary: what is clear, what is not, and a specific
              recommended next step.
            </p>
            <p className="mt-2 text-[12px] text-bos-charcoal/65 max-w-md">
              Clarity Kit clients can apply the amount paid toward this within 30 days.
            </p>
          </div>

          <div className="pt-8 border-t border-bos-ice">
            <p className="bos-eyebrow">Response times</p>
            <p className="mt-3 text-[14px] text-bos-charcoal/80 leading-[1.7]">
              We answer in working hours, in the order notes arrive. If you
              have an urgent commercial decision pending, please say so in the
              subject line and we will move it up.
            </p>
          </div>
        </div>

        <div id="ask" className="bg-bos-white border border-bos-ice rounded-sm p-8 lg:p-10 scroll-mt-28" data-testid="contact-form-card">
          {done ? (
            <div data-testid="contact-success">
              <p className="bos-eyebrow">Thank you</p>
              <h2 className="mt-4 text-2xl font-medium text-bos-navy">Your note has landed.</h2>
              <p className="mt-4 text-[14px] text-bos-charcoal/80 leading-[1.7]">
                We will reply within one working day. In the meantime, you can
                try Section 1 of the Clarity Kit or pick a time directly.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <Link to="/clarity-diagnostic" data-testid="contact-success-try" className="px-5 py-2.5 rounded-full bg-bos-ice text-bos-navy text-[14px] hover:bg-bos-light/60 transition-colors">
                  Try the Free Clarity Check
                </Link>
                <Link to="/booking" data-testid="contact-success-book" className="px-5 py-2.5 rounded-full bg-bos-navy text-bos-white text-[14px] hover:bg-bos-primary transition-colors">
                  Book a Diagnostic
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="c-name">Your name</label>
                  <input
                    id="c-name"
                    data-testid="contact-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="c-email">Email</label>
                  <input
                    id="c-email"
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="c-business">Business or professional context</label>
                <input
                  id="c-business"
                  data-testid="contact-business"
                  value={form.business}
                  onChange={(e) => setForm({ ...form, business: e.target.value })}
                  className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
                />
              </div>
              <div>
                <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="c-website">Website or LinkedIn (optional)</label>
                <input
                  id="c-website"
                  data-testid="contact-website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[12px] text-bos-charcoal/70 mb-1.5">Asking about</label>
                  <Select value={form.inquiry_type} onValueChange={(v) => setForm({ ...form, inquiry_type: v })}>
                    <SelectTrigger data-testid="contact-inquiry-trigger" className="bg-bos-white border-bos-ice">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {INQUIRY_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`contact-inquiry-option-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[12px] text-bos-charcoal/70 mb-1.5">Preferred next step</label>
                  <Select value={form.preferred_next_step} onValueChange={(v) => setForm({ ...form, preferred_next_step: v })}>
                    <SelectTrigger data-testid="contact-next-trigger" className="bg-bos-white border-bos-ice">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {NEXT_STEPS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`contact-next-option-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="c-message">What are you trying to clarify?</label>
                <textarea
                  id="c-message"
                  data-testid="contact-message"
                  rows={6}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px] resize-none"
                  placeholder="A few lines on what is on your mind, and where you are in the conversation."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-bos-navy text-bos-white px-7 py-3.5 rounded-full hover:bg-bos-primary transition-colors disabled:opacity-60 text-[14px]"
              >
                {submitting ? "Sending…" : "Send the note"}
                <ArrowRight size={14} />
              </button>
              <p className="text-[12px] text-bos-charcoal/60">
                We treat what you share with care. We do not share details with third parties.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
